<script lang="ts">
	import { app, os } from 'src/stores/session/native-bridge';
import { hideBluetoothSettingsToast } from 'src/stores/ui/calibration-modals';
import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { format } from 'svelte-i18n';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import OnboardingStepContainer from './OnboardingStepContainer.svelte';
	import { Alert, SelectCard, Text } from '@Shelly/shelly-design-system/index';
	import {
		control_bluetooth_device_ios,
		control_bluetooth_general_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_general_android,
	} from 'src/services/native_container_requests_android';
	import { real_id } from 'src/helpers/interface_helpers';
	import { calculateDistValues } from 'src/helpers/distance_helper';
	import DynamicAlert from 'src/components/DevicesSettings/SettingsGBLE/DynamicAlert.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	$: settingsStore = getDeviceSettings(device.id);
	export let device;
	export let deviceData;
	export let initialized = false;
	export let showExpiredMsg = false;

	deviceData.distanceRange = device?.distanceRange
		? `${device.distanceRange}`
		: deviceData.distanceRange;

	const range = {
		1: 'close',
		2: 'mid',
		3: 'close_mid',
		4: 'far',
		5: 'close_far',
		6: 'mid_far',
		7: 'full',
	};

	$: distanceUnit = device?.ht_ui?.unit || $settingsStore?.ht_ui?.unit || 'mm';

	$: closeStart = calculateDistValues(200, distanceUnit);
	$: closeEnd = calculateDistValues(450, distanceUnit, true);
	$: midStart = calculateDistValues(300, distanceUnit);
	$: midEnd = calculateDistValues(3500, distanceUnit, true);
	$: farStart = calculateDistValues(2500, distanceUnit);
	$: farEnd = calculateDistValues(5000, distanceUnit, true);
	$: zeroGraphValue = calculateDistValues(0, distanceUnit, true);
	$: minGraphValue = calculateDistValues(200, distanceUnit, true);
	$: oneGraphValue = calculateDistValues(1000, distanceUnit, true);
	$: twoGraphValue = calculateDistValues(2000, distanceUnit, true);
	$: threeGraphValue = calculateDistValues(3000, distanceUnit, true);
	$: fourGraphValue = calculateDistValues(4000, distanceUnit, true);

	$: minLabel = $format('min_two_hundred_mm', {
		default: 'Min {value} {unit}',
		values: { value: minGraphValue, unit: '' },
	});
	$: maxLabel = $format('max_five_thousand_mm', {
		default: 'Max {value} {unit}',
		values: { value: farEnd, unit: '' },
	});

	const setDynamicallyDistanceRange = (distanceRange) => {
		hideBluetoothSettingsToast.set(true);
		deviceData.distanceRange = distanceRange;
		if ($controlBleDevice.connectionState === 2) {
			$os === 'IOS'
				? control_bluetooth_general_ios(
						device.id,
						'setDistanceRange',
						'distanceRange',
						distanceRange,
					)
				: control_bluetooth_general_android(
						device.id,
						'setDistanceRange',
						'distanceRange',
						distanceRange,
					);
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};
</script>

<OnboardingStepContainer>
	<svelte:fragment slot="text">
		{#if !$app}
			<Alert
				type="warning"
				isHeadingBold
				heading={$format('alert_heading_ble_devices_restricted', {
					default: 'Bluetooth Restricted',
				})}
				message={$format('alert_message_ble_devices_restricted', {
					default:
						'Bluetooth devices can be configured only via the Shelly Smart Control application.',
				})}
				class="mb-s"
			/>
		{:else}
			<DynamicAlert {showExpiredMsg} />
		{/if}
		<Text styleType="body-small" class="mb-m">
			{$format('text_distance_range_limits', {
				default:
					'Set the distance range to define which distances are measured. Larger range limits cover more distance but use more battery power, with the last used range preselected by default or the full range if none was used.',
			})}
		</Text>
	</svelte:fragment>

	<svelte:fragment slot="left">
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => setDynamicallyDistanceRange('1')}
			title={$format('distance_range_option_close', { default: 'Close' })}
			class="mb-s width-full"
			descriptiveText={`${closeStart} - ${closeEnd}`}
			disabled={!initialized || !$app}
			selected={deviceData.distanceRange === '1'}
		/>
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => setDynamicallyDistanceRange('2')}
			title={$format('distance_range_option_mid', { default: 'Mid' })}
			class="mb-s width-full"
			descriptiveText={`${midStart} - ${midEnd}`}
			disabled={!initialized || !$app}
			selected={deviceData.distanceRange === '2'}
		/>
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => setDynamicallyDistanceRange('3')}
			title={$format('distance_range_option_close_mid', {
				default: 'Close and Mid',
			})}
			descriptiveText={`${closeStart} - ${midEnd}`}
			class="mb-s width-full"
			disabled={!initialized || !$app}
			selected={deviceData.distanceRange === '3'}
		/>
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => setDynamicallyDistanceRange('4')}
			title={$format('distance_range_option_far', { default: 'Far' })}
			descriptiveText={`${farStart} - ${farEnd}`}
			class="mb-s width-full"
			disabled={!initialized || !$app}
			selected={deviceData.distanceRange === '4'}
		/>
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => setDynamicallyDistanceRange('5')}
			title={$format('distance_range_option_close_far', {
				default: 'Close and Far',
			})}
			descriptiveText={`${closeStart} - ${closeEnd} ${$format('and', { default: 'and' })} ${farStart} - ${farEnd}`}
			class="mb-s width-full"
			disabled={!initialized || !$app}
			selected={deviceData.distanceRange === '5'}
		/>
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => setDynamicallyDistanceRange('6')}
			title={$format('distance_range_option_mid_far', {
				default: 'Mid and Far',
			})}
			descriptiveText={`${midStart} - ${farEnd}`}
			class="mb-s width-full"
			disabled={!initialized || !$app}
			selected={deviceData.distanceRange === '6'}
		/>
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => setDynamicallyDistanceRange('7')}
			title={$format('distance_range_option_full', {
				default: 'Full',
			})}
			descriptiveText={`${closeStart} - ${farEnd}`}
			class="width-full"
			disabled={!initialized || !$app}
			selected={deviceData.distanceRange === '7'}
		/>
	</svelte:fragment>

	<svelte:fragment slot="right">
		<div class="liquid-diagram-container">
			<div class="liquid-diagram">
				<img
					src={`./images/onboarding/distance/measurement_${range[deviceData.distanceRange] || 'full'}.png`}
					alt={$format('measurement_diagram', {
						default: 'Measurement diagram',
					})}
				/>
				<span class="liquid-label text-center">{@html minLabel}</span>
				<span class="liquid-label text-center">{zeroGraphValue}</span>
				<span class="liquid-label text-center">{oneGraphValue}</span>
				<span class="liquid-label text-center">{twoGraphValue}</span>
				<span class="liquid-label text-center">{threeGraphValue}</span>
				<span class="liquid-label text-center">{fourGraphValue}</span>
				<span class="liquid-label text-center">{@html maxLabel}</span>
			</div>
		</div>
	</svelte:fragment>
</OnboardingStepContainer>

<style>
	.liquid-diagram-container {
		display: flex;
		justify-content: end;
	}

	.liquid-diagram {
		position: relative;
		width: 203px;
		height: 242px;
	}

	.liquid-label {
		pointer-events: none;
		position: absolute;
		font-size: 10px;
		color: #6e6e6e;
	}

	.liquid-label:nth-of-type(1) {
		top: 5%;
		left: -15%;
	}
	.liquid-label:nth-of-type(2) {
		top: -1%;
		left: 53%;
	}
	.liquid-label:nth-of-type(3) {
		top: 19%;
		left: 43%;
	}
	.liquid-label:nth-of-type(4) {
		top: 38%;
		left: 50%;
	}
	.liquid-label:nth-of-type(5) {
		top: 56%;
		left: 56%;
	}
	.liquid-label:nth-of-type(6) {
		top: 75%;
		left: 63%;
	}
	.liquid-label:nth-of-type(7) {
		bottom: 1%;
		right: -6%;
	}

	@media (max-width: 639.98px) {
		.liquid-diagram-container {
			margin-left: 24px;
			margin-bottom: 24px;
			justify-content: center;
		}
	}
</style>
